import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Play, Pause, RotateCcw, X } from "lucide-react";

interface RestTimerProps {
  defaultRestSeconds?: number;
  onClose: () => void;
  onRestComplete?: (actualRestSeconds: number) => void;
}

export const RestTimer = ({ defaultRestSeconds = 60, onClose, onRestComplete }: RestTimerProps) => {
  const [timeLeft, setTimeLeft] = useState(defaultRestSeconds);
  const [isRunning, setIsRunning] = useState(true);
  const [totalTime, setTotalTime] = useState(defaultRestSeconds);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, timeLeft]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleReset = () => {
    setTimeLeft(totalTime);
    setIsRunning(false);
  };

  const handleChangeTime = (change: number) => {
    const newTime = Math.max(0, timeLeft + change);
    setTimeLeft(newTime);
    setTotalTime(Math.max(totalTime, newTime));
  };

  const progress = ((totalTime - timeLeft) / totalTime) * 100;

  const handleClose = () => {
    // Calculate actual rest time taken
    const actualRestSeconds = Math.floor((Date.now() - startTime) / 1000);
    if (onRestComplete) {
      onRestComplete(actualRestSeconds);
    }
    onClose();
  };

  return (
    <Card className="p-4 bg-gradient-card border-primary/30 shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">Rest Timer</h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleClose}
          className="h-6 w-6 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-muted rounded-full h-2 mb-4">
        <div
          className={`h-2 rounded-full transition-all duration-1000 ${
            timeLeft === 0 ? 'bg-green-600' : 'bg-primary'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Timer Display */}
      <div className={`text-center mb-4 ${timeLeft === 0 ? 'text-green-600' : 'text-foreground'}`}>
        <div className="text-4xl font-bold font-mono">
          {formatTime(timeLeft)}
        </div>
        {timeLeft === 0 && (
          <div className="text-sm font-semibold mt-1 animate-pulse">
            Rest Complete!
          </div>
        )}
      </div>

      {/* Time Adjustment Buttons */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleChangeTime(-15)}
          className="text-xs"
        >
          -15s
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleChangeTime(-30)}
          className="text-xs"
        >
          -30s
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleChangeTime(30)}
          className="text-xs"
        >
          +30s
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleChangeTime(15)}
          className="text-xs"
        >
          +15s
        </Button>
      </div>

      {/* Control Buttons */}
      <div className="grid grid-cols-3 gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsRunning(!isRunning)}
          className="flex items-center justify-center"
        >
          {isRunning ? (
            <>
              <Pause className="h-4 w-4 mr-1" />
              Pause
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-1" />
              Start
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleReset}
          className="flex items-center justify-center"
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          Reset
        </Button>
        <Button
          size="sm"
          variant="default"
          onClick={handleClose}
          className="bg-green-600 hover:bg-green-700"
        >
          Done
        </Button>
      </div>
    </Card>
  );
};
